/**
 * Disclosure — one-click "show me more" toggle.
 *
 * The report is built on progressive disclosure: a manager reading it for the
 * fifth time should not have to scroll past detail they already understand.
 * Every heavy block (score breakdown, dynamics, expert commentary on a risk)
 * hides behind exactly ONE of these — never two nested, which was the main
 * complaint about the original demo.
 *
 * Uncontrolled by default; pass `open` + `onToggle` when the parent needs to
 * own the state (e.g. an "expand all" affordance).
 *
 * Candidate for promotion into the design system (D2).
 *
 * @format
 */

import {useId, useState, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button, type ButtonVariant} from 'ui/components/atoms/Button';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {ControlHeightM} from 'ui/tokens/ts/sizing';

export interface DisclosureProps {
  /** Trigger label when collapsed. */
  label: string;
  /**
   * Вид кнопки. По умолчанию `ghost` — раскрытие внутри текста не должно
   * спорить с ним весом. `outline` там, где раскрытие само является итогом
   * блока: хвост списка расхода подводит черту под ним, а не поясняет
   * соседнюю строку, — но и действием первого порядка не является, и
   * заливка ставила бы его вровень с кнопками, которые что-то делают.
   */
  variant?: ButtonVariant;
  /** Trigger label when expanded. Defaults to `label`. */
  labelOpen?: string;
  /** Controlled open state. Omit for uncontrolled. */
  open?: boolean;
  onToggle?: (next: boolean) => void;
  /** Initial state when uncontrolled. */
  defaultOpen?: boolean;
  children?: ReactNode;
}

/**
 * Кнопка раскрытия — обычная системная `ghost`-кнопка базового размера.
 *
 * Раньше здесь была своя вёрстка на `ButtonBase`: свой кегль, свои
 * отступы, свой hover. Это давало контрол, которого нет в системе —
 * синий текст без синей кнопки, — и он выпадал из ряда всех остальных
 * кнопок страницы и по размеру, и по поведению.
 */
const Trigger = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$variant: ButtonVariant}>(({$variant}) => ({
  alignSelf: 'flex-start',
  // По одной вертикали с текстом выравнивается РАЗНОЕ. У ghost-кнопки края
  // нет, и на вертикаль встаёт подпись — её и подтягиваем, вычитая
  // невидимый padding. У кнопки с краем (solid, outline) на вертикаль
  // обязан встать он: сдвинутая рамка читается как выступ, а не как
  // выравнивание.
  marginLeft: $variant === 'ghost' ? -spacingPx('m') : 0,

  // Подпись раскрытия — фраза, а не глагол: «Чем правки специалиста
  // отличаются от операций автоматики» на узком экране не помещается в
  // строку никакой ширины. Системная кнопка держит подпись в одну строку
  // при фиксированной высоте — это верно для действия, но не для фразы,
  // и на телефоне такая кнопка растягивала документ на 122 px вбок.
  //
  // Разрешаем перенос без порога: пока подпись помещается, высота равна
  // прежней и ничего не меняется; когда не помещается — кнопка растёт
  // вниз, а не вбок.
  maxWidth: '100%',
  '& > button': {
    height: 'auto',
    minHeight: ControlHeightM,
    whiteSpace: 'normal',
    textAlign: 'left',
    paddingTop: spacingPx('xs'),
    paddingBottom: spacingPx('xs'),
  },
}));

export function Disclosure({
  label,
  variant = 'ghost',
  labelOpen,
  open: openProp,
  onToggle,
  defaultOpen = false,
  children,
}: DisclosureProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const contentId = useId();
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const handleClick = () => {
    const next = !open;
    if (!isControlled) setInternalOpen(next);
    onToggle?.(next);
  };

  return (
    <Stack gap='s'>
      <Trigger $variant={variant}>
        <Button
          size='m'
          variant={variant}
          color='primary'
          rightIcon={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          onClick={handleClick}
          aria-expanded={open}
          aria-controls={contentId}
        >
          {open ? (labelOpen ?? label) : label}
        </Button>
      </Trigger>
      {open && <Box id={contentId}>{children}</Box>}
    </Stack>
  );
}
